"use client";

import { toast as sonnerToast } from "sonner";
import { isNavDebugAllowedEmail } from "@/lib/navigation/debug-allowlist";

export const TOASTS_ENABLED = process.env.NEXT_PUBLIC_TOASTS_ENABLED === "true";
const TOAST_OVERRIDE_STORAGE_KEY = "toast-manual-override";

type ToastType = typeof sonnerToast;

const DEBUG_TOAST_EMAILS = new Set([
  "stefan.andonov1@yahoo.com",
  "stefff@yahoo.com",
]);

let debugToastEmail: string | null = null;
let manualOverride: boolean | null = null;

function normalizeEmail(email: string | null | undefined): string | null {
  const normalized = (email || "").trim().toLowerCase();
  return normalized || null;
}

function getToastOverrideStorageKey(email: string): string {
  return `${TOAST_OVERRIDE_STORAGE_KEY}:${email}`;
}

function readStoredToastOverride(email: string | null): boolean | null {
  if (typeof window === "undefined" || !email || !isNavDebugAllowedEmail(email)) return null;
  const stored = window.localStorage.getItem(getToastOverrideStorageKey(email));
  if (stored === "true") return true;
  if (stored === "false") return false;
  return null;
}

export function enableDebugToasts(email: string) {
  debugToastEmail = normalizeEmail(email);
  manualOverride = readStoredToastOverride(debugToastEmail);

  if (typeof window !== "undefined") {
    window.localStorage.removeItem(TOAST_OVERRIDE_STORAGE_KEY);
  }
}

export function setToastOverride(enabled: boolean | null) {
  if (!debugToastEmail || !isNavDebugAllowedEmail(debugToastEmail)) {
    manualOverride = null;
    return;
  }

  manualOverride = enabled;
  if (typeof window === "undefined") return;
  const storageKey = getToastOverrideStorageKey(debugToastEmail);
  window.localStorage.removeItem(TOAST_OVERRIDE_STORAGE_KEY);
  if (enabled === null) {
    window.localStorage.removeItem(storageKey);
    return;
  }
  window.localStorage.setItem(storageKey, String(enabled));
}

export function getToastOverride(): boolean | null {
  if (!debugToastEmail || !isNavDebugAllowedEmail(debugToastEmail)) return null;
  return manualOverride;
}

function isToastEnabled(): boolean {
  if (debugToastEmail && isNavDebugAllowedEmail(debugToastEmail) && manualOverride !== null) {
    return manualOverride;
  }
  if (TOASTS_ENABLED) return true;
  if (
    process.env.NEXT_PUBLIC_DEBUG_TOASTS === "true" &&
    debugToastEmail &&
    DEBUG_TOAST_EMAILS.has(debugToastEmail)
  ) {
    return true;
  }
  return false;
}

export function getToastEnabled(): boolean {
  return isToastEnabled();
}

export const toast = new Proxy(sonnerToast, {
  apply(_target, thisArg, args) {
    if (!isToastEnabled()) return "";
    return Reflect.apply(sonnerToast, thisArg, args);
  },
  get(_target, prop, receiver) {
    if (!isToastEnabled()) {
      return () => "";
    }
    return Reflect.get(sonnerToast, prop, receiver);
  },
}) as ToastType;
